// src/belediye/pages/sohbet/SohbetlerPage.js
//
// FAZ-94 — SOHBET LİSTESİ ARTIK BİR SAYFA (yalnız telefonda).
//
// Kullanıcı: "Kurum içi sohbetler kısmı bir sayfa gibi hareket etsin: yukarıda
// navbarda 'Kurum İçi Sohbet' yazsın, sol tarafta menü, sağ tarafta bildirim
// olsun." Yani sıradan bir liste sayfası — diğerlerinden farkı yok:
// `PageHeader` adı navbara yazar (FAZ-92), alt bar durur, çipler yapışır.
//
// TABLET VE MASAÜSTÜNDE BU SAYFAYA GELİNMEZ (K-1): orada `ChatDock`'un yüzen
// penceresi liste + sohbeti YAN YANA gösteriyor; rotaya çevirmek kazanç değil
// kayıp olurdu. Rota yine de erişilebilir kalır (derin link, yer imi); geniş
// ekranda açılırsa liste tek sütun çizilir ve satır seçilince pencere açılır.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";

import PageHeader from "../../components/utils/PageHeader";
import SohbetListesi from "../../components/mesajlasma/SohbetListesi";
import {
  ConvSegments,
  GorevAltSuzgec,
} from "../../components/mesajlasma/ChatDock";
import {
  visibleSegments,
  gorevKovaSayilari,
} from "../../components/mesajlasma/sohbetKovalari";
import useMsjFeatures from "../../components/mesajlasma/useMsjFeatures";
import { fetchConversations } from "../../services/mesajlasmaApi";
import useKatman from "../../hooks/useKatman";
import { openChat } from "../../services/mesajlasmaBridge";
import "../../components/mesajlasma/chat.css";
import "./sohbet.css";

export default function SohbetlerPage() {
  const navigate = useNavigate();
  const telefon = useKatman() === "telefon";
  const msjFeatures = useMsjFeatures();

  const [convs, setConvs] = useState(null);
  const [segment, setSegment] = useState(null);
  const [gorevAlt, setGorevAlt] = useState("acik");

  const yukle = useCallback(async () => {
    try {
      setConvs(await fetchConversations());
    } catch {
      setConvs((prev) => prev || []);
    }
  }, []);

  /* FAZ-65 — periyodik yoklama YOK. Tetikler: sayfaya giriş, sekmeye dönüş,
     pencere odağı. Sohbetin İÇİNDE (detay sayfası) yoklama sürüyor; liste
     ekranda dururken saniyede bir istek atmak sistemin kaldırdığı şeydi. */
  useEffect(() => {
    yukle();
    const onVis = () => !document.hidden && yukle();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [yukle]);

  const segs = visibleSegments(msjFeatures, convs);
  const firstSegKey = segs[0]?.key || null;
  useEffect(() => {
    setSegment((cur) => (cur === null ? firstSegKey : cur));
  }, [firstSegKey]);

  /* FAZ-94 madde 14 — YAPIŞKAN OLAN NE?
     Kullanıcı: "eğer sadece taleplerim varsa yukarıda asılı kalan şey
     Aktif/Onay sekmeleri olsun; ama kişiler veya sohbetler de varsa hem
     segment şeridi hem alt süzgeç sabit kalsın."

     İkisi de PageHeader'ın TEK yapışkan kabının içinde: `tabs` ve
     `belowToolbar`. Ayrı ayrı `position: sticky` verilseydi FAZ-89'un dersi
     tekrarlanırdı — sticky yalnız KAPSAYICISI ekrandayken yapışır; kısa kap
     ekrandan çıkınca ikisi birden giderdi. */
  const altSuzgecGorunur = segment === "gorev";
  const altSuzgec = altSuzgecGorunur ? (
    <GorevAltSuzgec
      value={gorevAlt}
      onChange={setGorevAlt}
      sayilar={gorevKovaSayilari(convs)}
    />
  ) : null;
  const tekSegment = segs.length <= 1;

  const sohbetAc = (c) => {
    /* Telefonda gerçek rota; geniş ekranda pencere köprüsü. Tek düğme iki
       yüzeyi de doğru yere götürür — çağıranın katman bilmesi gerekmiyor. */
    if (telefon) navigate(`/yonetim/sohbet/${c.id}`);
    else openChat(c.id, c.title);
  };

  return (
    <div className="sohbet-sayfa">
      <PageHeader
        title="Kurum İçi Sohbet"
        /* FAZ-97 madde 14 — "YENİ" ŞERİDİN YANINA İNDİ.
           Kullanıcı: "sohbetlerde Yeni butonu var ya, bunu bir alt satıra
           alalım ve Talepler yazısını yanına, sağ tarafa almamız gerekmekte."

           Yani araç satırındaki tek başına duran düğme ile çip şeridi TEK
           satırda birleşiyor: [ çipler ................ ] [ ＋ Yeni ].
           Şerit esner, düğme sabit genişlikte sağda kalır — düğme kaydırmayla
           gözden kaybolmaz.

           Tek segment varsa şerit zaten çizilmiyor (ConvSegments <=1'de null
           döner); o durumda yapışkan yuvaya doğrudan alt süzgeç girer. */
        tabs={
          <div className="sohbet-serit">
            <div className="sohbet-serit-cipler">
              {tekSegment ? (
                altSuzgec
              ) : (
                <ConvSegments
                  segments={segs}
                  value={segment}
                  onChange={setSegment}
                />
              )}
            </div>
            {(msjFeatures.serbest || msjFeatures.gorev) && (
              <button
                type="button"
                className="msj-newbtn sohbet-yeni-btn"
                onClick={() => navigate("/yonetim/sohbet/yeni")}
              >
                <PlusIcon /> Yeni
              </button>
            )}
          </div>
        }
        belowToolbar={tekSegment ? null : altSuzgec}
        className="sohbet-sticky"
      />

      {segs.length === 0 ? (
        <div className="msj-empty sohbet-bos">
          Şu anda açık bir sohbet özelliği yok.
        </div>
      ) : (
        <SohbetListesi
          convs={convs}
          segment={segment}
          gorevAlt={gorevAlt}
          onAc={sohbetAc}
          className="sohbet-liste"
        />
      )}
    </div>
  );
}
